import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  signal,
  ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSidenavModule } from "@angular/material/sidenav";
import { ActivatedRoute } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { CardViz } from "../../models/well.model";
import { NGIVAAPService } from "../../services/ivaap.service";
import { NodeService } from "../../services/node.service";
import { WellService } from "../../services/well.service";
import { InfiniteWorld } from "../../utils/infinite-world";
import { CanvasContextMenu, CanvasNode } from "../canvas/canvas-models";
import { IvaapComponent } from "../ivaap/ivaap.component";

@Component({
  selector: "app-canvas",
  imports: [MatSidenavModule, MatButtonModule, MatProgressSpinnerModule, IvaapComponent],
  templateUrl: "./canvas.html",
  styleUrl: "./canvas.scss",
})
export class Canvas implements AfterViewInit, OnDestroy {
  @ViewChild("worldContainer", { static: false })
  worldContainer?: ElementRef<HTMLDivElement>;

  // Sidenav state
  sidenavOpened = signal(false);
  sidenavContext = signal<{
    label: string;
    originCardId: string;
    cardViz: CardViz;
  } | null>(null);
  isLoading = signal(false);

  private world: InfiniteWorld = new InfiniteWorld((dashboardId) =>
    this.handleResurrect(dashboardId),
  );
  private selectionCheckInterval?: number;
  private router = inject(ActivatedRoute);
  private wellService = inject(WellService);
  private nodeService = inject(NodeService);
  private routeParams = toSignal(this.router.paramMap);
  wellId = computed(() => this.routeParams()?.get("wellId") ?? null);
  well = computed(() =>
    this.wellService.loadedWells.find((w) => w.name === this.wellId()),
  );
  private viewInitialized = signal(false);

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private ivaapSvc: NGIVAAPService,
  ) {
    effect(async () => {
      if (!this.viewInitialized()) {
        return;
      }

      const wellId = this.wellId();
      if (!wellId) {
        return;
      }

      await this.showRootContextSidePanel();
    });
  }

  async handleResurrect(dashboardId: string): Promise<void> {
    console.log("Resurrecting dashboard with ID:", dashboardId);
  }

  ngAfterViewInit(): void {
    if (this.worldContainer) {
      this.world.attach(this.worldContainer.nativeElement);
      this.startSelectionMonitoring();
    }
    this.viewInitialized.set(true);
  }

  ngOnDestroy(): void {
    this.stopSelectionMonitoring();
    this.world.detach();
  }

  /**
   * Monitor selection changes to update UI
   */
  private startSelectionMonitoring(): void {
    this.ngZone.runOutsideAngular(() => {
      this.selectionCheckInterval = window.setInterval(() => {
        this.ngZone.run(() => {
          this.cdr.markForCheck();
        });
      }, 100);
    });
  }

  /**
   * Stop monitoring selection changes
   */
  private stopSelectionMonitoring(): void {
    if (this.selectionCheckInterval) {
      clearInterval(this.selectionCheckInterval);
    }
  }

  /**
   * Add a new element to the world
   */
  async addElement(
    entityId: string,
    entityType: string,
    imageUrl: string,
    dashboardId: string,
    contextText: string,
  ): Promise<void> {
    const contextMenu = await this.getNextNodesAsContextMenu(entityId, entityType);

    const canvasElement: CanvasNode = {
      context: contextMenu,
      footer: contextText,
      entityId: entityId,
      entityType: entityType,
      imageUrl: imageUrl,
      dashboardId: dashboardId,
    };

    this.addCanvasNode(canvasElement, this.getNextPosition());
  }

  /**
   * Re-center the world to fit all elements
   */
  recenter(): void {
    this.world.recenter();
  }

  /**
   * Reset zoom to 100%
   */
  resetZoom(): void {
    this.world.setZoom(1);
  }

  /**
   * Get the currently selected element ID for display
   */
  getSelectedElementId(): string | null {
    return this.world.getSelectedElementId();
  }

  /**
   * Common method to add a canvas node
   */
  private addCanvasNode(
    canvasNode: CanvasNode,
    position: { x: number; y: number; parentId?: string },
  ): string {
    const element = this.world.createCanvasElement(canvasNode);
    return this.world.addElement(
      element,
      position.x,
      position.y,
      200,
      250,
      position.parentId,
      canvasNode,
    );
  }

  /**
   * Calculate next position for new elements
   */
  private getNextPosition(): { x: number; y: number; parentId?: string } {
    const selectedId = this.world.getSelectedElementId();

    if (selectedId) {
      const selectedElement = this.world.getElement(selectedId);
      if (selectedElement) {
        return {
          x: selectedElement.x + selectedElement.width + 100,
          y: selectedElement.y + 50,
          parentId: selectedId,
        };
      }
    }

    return {
      x: Math.random() * 800 - 200,
      y: Math.random() * 600 - 200,
    };
  }

  /**
   * Clear all elements
   */
  clearAll(): void {
    const elements = this.world.getAllElements();
    elements.forEach((el) => this.world.removeElement(el.id));
  }

  /**
   * Get the current context menu from the service and reassign actions
   */
  async getNextNodesAsContextMenu(entityId: string, entityType: string): Promise<CanvasContextMenu> {
    this.isLoading.set(true);
    try {
      const card = await firstValueFrom(this.wellService.getCard(entityId, entityType));
      if(!card || !card.data) {
        return { items: [] };
      }
      const menu: CanvasContextMenu = {items: card.data.map((data) => ({
        label: data.type_id,
        action: async () => {
          this.isLoading.set(true);
          try {
            const cardViz = await firstValueFrom(this.wellService.getCardDetails(data.type_id));
            this.openSidenavWithContext(
              data.type_id,
              data.osdu_id,
              cardViz,
            );
          } finally {
            this.isLoading.set(false);
          }
        },
      }))};
      return menu;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Open the sidenav with context
   */
  openSidenavWithContext(
    label: string,
    originCardId: string,
    cardViz: CardViz,
  ): void {
    this.sidenavContext.set({
      label,
      originCardId,
      cardViz: cardViz,
    });
    this.sidenavOpened.set(true);
  }

  /**
   * Close the sidenav
   */
  closeSidenav(): void {
    this.sidenavOpened.set(false);
    // Clear context after a brief delay to avoid flickering
    setTimeout(() => this.sidenavContext.set(null), 300);
  }

  /**
   * Add element to canvas and close sidenav
   */
  async addToCanvas(): Promise<void> {
    const context = this.sidenavContext();
    if (!context) return;

    const contextText = `Sample reply: OK‑53 is one of the wells that make up the Okan
            Field, a long‑producing offshore oil and gas field in Nigeria that
            is developed through clusters of wells spread across distinct
            structural areas. Within the field, OK‑53 sits in the Main Block,
            contributing to production from the central part of this large,
            fault‑segmented field alongside many neighboring wells.`;

    const currentEntityType = context.label;

    const payload = {
      name: `oro-dashboard-${Date.now()}`,
    };

    // const response = await this.ivaapSvc.IVAAPService?.executeUCCommand(
    //   "saveDashboard",
    //   payload,
    // );
    // console.log("saveDashboard response:", response);
    // const dashboardId = response?.id;

    // if (!dashboardId) {
    //   console.error("Failed to create dashboard in IVAAP.");
    //   return;
    // }

    const imageData = await this.ivaapSvc.IVAAPService?.screenshot("");
    this.ivaapSvc.IVAAPService?.detach();

    if (!imageData) {
      console.error("Failed to capture screenshot from IVAAP.");
      return;
    }
    await this.addElement(
      context.originCardId,
      currentEntityType,
      imageData,
      "",
      contextText,
    );
    this.closeSidenav();
  }

  async showRootContextSidePanel(): Promise<void> {
    this.isLoading.set(true);
    try {
      const wellCard = await firstValueFrom(this.wellService.getCard(this.well()?.name ?? ""));
      const wellCardViz = await firstValueFrom(this.wellService.getCardDetails(this.well()?.name ?? ""))
      this.openSidenavWithContext(
        "Well Context",
        wellCard.id,
        wellCardViz,
      );
    } finally {
      this.isLoading.set(false);
    }
  }
}
